import { CheckCircle2, Clock, Layers } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";

export const STOCK_STATES = {
  ready: {
    label: "Ready Stock",
    hint: "Dispatched within 24 hours from Chickpet",
    icon: CheckCircle2,
    light: { bg: "#EDF2ED", text: "#2F4A38", border: "#CFE0D3", dot: "#3F7A54" },
    dark: { bg: "#15201A", text: "#A9D5B8", border: "#27402F", dot: "#4E9C6A" },
  },
  low: {
    label: "Low Stock",
    hint: "Limited running metres — reserve on WhatsApp",
    icon: Clock,
    light: { bg: "#FBF3E8", text: "#7A4A12", border: "#EFD9BB", dot: "#C07A1E" },
    dark: { bg: "#241A0F", text: "#E8C58C", border: "#43331D", dot: "#D49338" },
  },
  made_to_order: {
    label: "Made to Order",
    hint: "Mill dyed to your shade — 7 to 10 days",
    icon: Layers,
    light: { bg: "#F4EFE6", text: "#5C5347", border: "#DED6C7", dot: "#8A5A24" },
    dark: { bg: "#1E1B17", text: "#D6CCC0", border: "#37302A", dot: "#B87333" },
  },
};

const useTone = () => (useTheme().theme === "dark" ? "dark" : "light");

export const StockBadge = ({ status = "ready", size = "sm", showIcon = true, className = "" }) => {
  const state = STOCK_STATES[status] || STOCK_STATES.ready;
  const tone = state[useTone()];
  const Icon = state.icon;
  const small = size === "sm";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border whitespace-nowrap ${
        small ? "px-2.5 py-1 text-[10px]" : "px-3.5 py-1.5 text-xs"
      } ${className}`}
      style={{
        backgroundColor: tone.bg,
        color: tone.text,
        borderColor: tone.border,
        fontFamily: "'Manrope', sans-serif",
        fontWeight: 600,
        letterSpacing: "0.06em",
      }}
      title={state.hint}
      data-testid={`stock-badge-${status}`}
    >
      <span
        className={`rounded-full shrink-0 ${status === "ready" ? "stock-dot-pulse" : ""} ${
          small ? "w-1.5 h-1.5" : "w-2 h-2"
        }`}
        style={{ backgroundColor: tone.dot }}
      />
      {showIcon && !small && <Icon className="w-3.5 h-3.5" />}
      {state.label.toUpperCase()}
    </span>
  );
};

export const StockLegend = ({ className = "" }) => {
  const tone = useTone();
  return (
    <div
      className={`flex flex-wrap items-center gap-x-6 gap-y-2 ${className}`}
      data-testid="stock-legend"
    >
      <span
        className="text-[10px] uppercase tracking-[0.2em] font-bold"
        style={{ color: "var(--mp-copper)", fontFamily: "'Manrope', sans-serif" }}
      >
        Stock guide
      </span>
      {Object.entries(STOCK_STATES).map(([key, state]) => (
        <span key={key} className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: state[tone].dot }}
          />
          <span
            className="text-[11px]"
            style={{ color: "var(--mp-muted)", fontFamily: "'Manrope', sans-serif" }}
          >
            <span style={{ color: "var(--mp-text)", fontWeight: 600 }}>{state.label}</span>
            {" — "}
            {state.hint}
          </span>
        </span>
      ))}
    </div>
  );
};
